import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormControl,
} from "@angular/forms";
import {
  BestellingDorpelDto,
  BestellingDto,
  DealerDto,
} from "../shared/api-models/models";
import { BackendService } from "../core/services/backend.service";
import { FormComponent } from "../shared/form-component";
import { ActivatedRoute, Route, Router } from "@angular/router";
import { DataStateService } from "../core/services/data-state.service";
import { GraphicsService } from "../core/services/graphics.service";
import { NgbPanelChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { BaseComponent } from "../shared/base.component";
import { takeUntil, catchError } from "rxjs/internal/operators";
import { UiStateService } from "../core/services/ui-state-service";
import { NgxSpinnerService } from "ngx-spinner";
import { environment } from "../../environments/environment";
import { FileFormatValidator } from "../shared/file-format-validator";
import { Observable, throwError } from "rxjs";
import { ProcessingResponse } from "../shared/api-models/processing-response";
import { Guid } from "guid-typescript";
import { formatDate } from "@angular/common";
import { logoBase64 } from "./constants";
import { DEALER_RESOURCES_STORAGE_KEY } from "../dealers/custom-dealer-resource-map";
import { DEFAULT_CROSS_PORTAL_LINK } from "../shared/constants";

const WRONG_FILE_STRUCTURE_ERROR =
  "Dit is geen geldig Matrix/Groenveld bestand. Voer een geldig bestand in";
const PRESERVED_FORM_FIELDS = [
  "opdrachtgever",
  "contactpersoon",
  "telefoonnummer",
  "emailadres",
  "uwReferentie",
  "dealerCode",
];
@Component({
  selector: "app-welkom",
  templateUrl: "./welkom.component.html",
  styleUrls: ["./welkom.component.scss"],
})
export class WelkomComponent extends BaseComponent implements OnInit {
  public formGroup: FormGroup;
  public dealers: DealerDto[] = [];
  public productType: string;
  public prefilledOrderErrorMessage: string | null = null;
  public posted: boolean;
  public crossPortalLink: string = DEFAULT_CROSS_PORTAL_LINK;

  public get model(): BestellingDto {
    return this.dataStateService.getModel();
  }

  public get minDeliveryDate(): string {
    return formatDate(new Date(), "yyyy-MM-dd", "en");
  }

  private _formComponent: FormComponent;

  constructor(
    private formBuilder: FormBuilder,
    private backend: BackendService,
    private router: Router,
    private dataStateService: DataStateService,
    private uiState: UiStateService,
    private spinner: NgxSpinnerService,
    private cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private graphics: GraphicsService
  ) {
    super();
  }

  getSortedDealersList(originalList) {
    let sortedList = [...originalList];
    sortedList.sort((d1, d2) => d1.naam.localeCompare(d2.naam));

    return sortedList;
  }

  ngOnInit(): void {
    this.dataStateService.homePath = this.router.url;
    this.productType = this.activatedRoute.snapshot.paramMap.get("productType");
    this.uiState.setProductType(this.productType);
    this.populatePreservedWelkomFormValues(PRESERVED_FORM_FIELDS);

    const cachedDealerResources = sessionStorage.getItem(DEALER_RESOURCES_STORAGE_KEY);

    if (cachedDealerResources) {
      const dealerResources = JSON.parse(cachedDealerResources);
      this.crossPortalLink = this.crossPortalLink + `/welkom/${dealerResources.dealerGUID}`;
    }

    this.updateFormData();
    let guidAssociatedDealers = this.uiState.currentDealersGroup;

    if (guidAssociatedDealers) {
      this.dealers = this.getSortedDealersList(guidAssociatedDealers);
      let currentlySelectedDealerCode = this.formGroup.get("dealerCode").value;

      if (!currentlySelectedDealerCode) {
        this.setFirtDealerFormGuidAssociatedList(guidAssociatedDealers);
      } else {
        let dealerFromGuidAssociatedList = guidAssociatedDealers.filter(
          (dealer) => {
            return dealer.dealerCode === currentlySelectedDealerCode;
          }
        );

        if (!dealerFromGuidAssociatedList.length) {
          this.setFirtDealerFormGuidAssociatedList(guidAssociatedDealers);
        }
      }
    } else {
      this.backend
        .getDealers()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.dealers = this.getSortedDealersList(result);
        });
    }

    setTimeout(() => {
      this.cdRef.detectChanges();
    }, 100);
  }

  private setFirtDealerFormGuidAssociatedList(guidAssociatedDealers) {
    this.formGroup
      .get("dealerCode")
      .setValue(guidAssociatedDealers[0].dealerCode);
    this.preserveWelkomFormValues(["dealerCode"]);
  }

  private updateFormData() {
    this.formGroup = this.buildForm();
    this._formComponent = new FormComponent(this.formGroup);
  }

  get bijlage(): FormArray {
    return this.formGroup.get("bijlage") as FormArray;
  }

  get isFromPrefilledOrder(): boolean {
    return !!this.uiState?.fromPrefilledOrder;
  }

  deleteFile(index: number): void {
    this.bijlage.removeAt(index);
    if (this.model.files[index]) {
      this.model.files.splice(index, 1);
    }
    this.cdRef.detectChanges();
  }

  async onFileSelect(event, index) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.model.files[index] = file;
    }
  }

  addFileUploadControl(): void {
    this.bijlage.push(
      this.formBuilder.control(["", [FileFormatValidator.validate]])
    );
    this.cdRef.detectChanges();
  }

  public showError(key: string, index?: number): boolean {
    let shouldShowError = this._formComponent.showError(key, index);
    return shouldShowError;
  }

  public errorMessage(key: string, index?: number): string {
    let formControl;

    if (index !== null && typeof index !== "undefined") {
      let formArray = this.formGroup.get(key) as FormArray;
      formControl = formArray.at(index);
    } else {
      formControl = this.formGroup.get(key);
    }

    if (formControl.valid) {
      return "";
    }

    const [firstError] = Object.keys(formControl.errors);
    const error = ValidationMessages[key]
      ? ValidationMessages[key][firstError]
      : null;
    return error || this._formComponent.errorMessage(key);
  }

  public onDorpelAddButtonClicked() {
    if (!this.formGroup.valid && !environment.disableFieldValidation) {
      Object.keys(this.formGroup.controls).forEach((field) => {
        const control = this.formGroup.get(field);
        control.markAsTouched();
      });
      return;
    }
    this.saveChangesToPostModel();
    this.router.navigate(["dorpel"], { relativeTo: this.activatedRoute });
  }

  public onEditDorpel(dorpel: BestellingDorpelDto): void {
    if (!this.validateForm()) {
      return;
    }

    this.saveChangesToPostModel();
    this.router.navigate(["dorpel"], {
      queryParams: { dorpelGuid: dorpel.guid },
      relativeTo: this.activatedRoute,
    });
  }

  public onDorpelBestellenButtonClicked() {
    if (!this.validateForm()) {
      return;
    }

    this.posted = true;
    if (this.cartItems.length < 1) {
      return;
    }

    this.model.dorpels.forEach((dorpel) => {
      if (!dorpel.drawing) {
        this.addOffscreenDrawing(dorpel);
      }
    });
    this.model.logoImageData = logoBase64;

    this.saveChangesToPostModel();
    this.router.navigate(["overzicht"], { relativeTo: this.activatedRoute });
  }

  public onDealerChanged(): void {
    this.model.dealerCode = this.formGroup.get("dealerCode").value;
    this.preserveWelkomFormValues(["dealerCode"]);
  }

  private addOffscreenDrawing(dorpel: BestellingDorpelDto): void {
    let offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = dorpel.zaagmaat;
    offscreenCanvas.height = 260;
    offscreenCanvas.id = "offscreenCanvas";
    this.graphics.initializeDrawing("offscreenCanvas", dorpel.zaagmaat);
    this.graphics.drawDorpel(dorpel, offscreenCanvas); //draw at scale 1 for correct fit

    let imageData = offscreenCanvas.toDataURL("image/png");
    dorpel.drawing = imageData;
  }

  private getDummyJsonModel(): BestellingDto {
    const json =
      '{"dorpels":[{"guid":"b2bdcf82-7d97-5113-acbc-cdef3248eced","aantal":2,"aantalNeuten":2,"aantalSluitpotten":2,"dorpelCode":"BI.5757.B","merk":"rega","zaagmaat":500,"neuten":[{"daagmaat":0,"neutCode":"N5757.L67.B","positie":"Links","sponningmaat":"53mm","sponningsoort":"Sponning"},{"daagmaat":0,"neutCode":"N5757.R67.B","positie":"Rechts","sponningmaat":"53mm","sponningsoort":"Sponning"}],"maatA":0,"maatZ":0,"sluitpotten":[{"maat":0,"sluitpotCode":"SPB.BI"},{"maat":333,"sluitpotCode":"SPB.BI.HMB"}],"bewerkingen":[{"bewerkingCode":"9200"},{"bewerkingCode":"9201"}]},{"guid":"b2bdcf82-7d97-5113-acbc-cdef3248e999","aantal":2,"aantalNeuten":2,"aantalSluitpotten":2,"dorpelCode":"BI.5757.B","merk":"SSAA","zaagmaat":500,"neuten":[{"daagmaat":0,"neutCode":"N5757.L67.B","positie":"Links","sponningmaat":"53mm","sponningsoort":"Sponning"},{"daagmaat":0,"neutCode":"N5757.R67.B","positie":"Rechts","sponningmaat":"53mm","sponningsoort":"Sponning"}],"maatA":0,"maatZ":0,"sluitpotten":[{"maat":0,"sluitpotCode":"SPB.BI"},{"maat":333,"sluitpotCode":"SPB.BI.HMB"}],"bewerkingen":[{"bewerkingCode":"9200"},{"bewerkingCode":"9201"}]}],"opdrachtgever":"Andrei Ciobanu","contactpersoon":"Andrei Ciobanu","telefoonnummer":"0755638595","emailadres":"bliulinx@gmail.com","uwReferentie":"asdasd","dealerCode":"CODE","opmerkingen":"asdasd","bijlage":"file.jpg"}';
    return <BestellingDto>JSON.parse(json);
  }

  public get cartItems(): BestellingDorpelDto[] {
    return this.dataStateService.getModel().dorpels;
  }

  public onDeleteDorpel(dorpel: BestellingDorpelDto): void {
    const index = this.model.dorpels.findIndex((d) => d.guid === dorpel.guid);
    this.model.dorpels.splice(index, 1);
  }

  onPanelChanged($event: NgbPanelChangeEvent, acc) {}

  async onPrefilledOrderformFileSelected(event) {
    if (event.target.files.length > 0) {
      this.prefilledOrderErrorMessage = null;

      this.spinner.show();
      const file = event.target.files[0];
      this.model.prefilledOrderformFile = file;
      this.getPrefilledOrderform(file)
        .pipe(
          catchError((err: any) => {
            this.spinner.hide();
            if (err.error.indexOf("WrongFileStructureException") >= 0) {
              this.prefilledOrderErrorMessage = WRONG_FILE_STRUCTURE_ERROR;
            }
            return throwError(err);
          })
        )
        .subscribe((result: ProcessingResponse) => {
          this.uiState.fromPrefilledOrder = true;
          result.order.id = Guid.create().toString();
          result.order.dorpels.forEach(
            (d) => (d.guid = Guid.create().toString())
          );
          result.order.prefilledOrderformFile = file;
          // this.populatePreservedWelkomFormValues(PRESERVED_FORM_FIELDS); TODO: check if import overrides
          this.overrideWelkomFormValues(result.order);
          this.dataStateService.setModel(result.order);
          this.updateFormData();
          this.uiState.setNeutenChoices(result.neuten);
          this.spinner.hide();
        });
    }
  }

  private preserveWelkomFormValues(fieldNameList: string[]): void {
    fieldNameList.map((fieldName: string) => {
      let fieldValue: string = this.formGroup.get(fieldName).value;
      let lsKey: string = `welkomformvalue_${fieldName}`;

      if (fieldValue) {
        localStorage.setItem(lsKey, fieldValue);
      }
    });
  }

  private populatePreservedWelkomFormValues(fieldNameList: string[]): void {
    fieldNameList.map((fieldName: string) => {
      let lsKey: string = `welkomformvalue_${fieldName}`;
      let previouslySavedValue = localStorage.getItem(lsKey);

      if (previouslySavedValue) {
        this.model[fieldName] = previouslySavedValue;
      }
    });
  }

  private overrideWelkomFormValues(fileProcessingResultOrder): void {
    fileProcessingResultOrder.opdrachtgever =
      this.formGroup.get("opdrachtgever").value ||
      fileProcessingResultOrder.opdrachtgever;
    fileProcessingResultOrder.contactpersoon =
      this.formGroup.get("contactpersoon").value ||
      fileProcessingResultOrder.contactpersoon;
    fileProcessingResultOrder.telefoonnummer =
      this.formGroup.get("telefoonnummer").value ||
      fileProcessingResultOrder.telefoonnummer;
    fileProcessingResultOrder.emailadres =
      this.formGroup.get("emailadres").value ||
      fileProcessingResultOrder.emailadres;
    fileProcessingResultOrder.uwReferentie =
      this.formGroup.get("uwReferentie").value ||
      fileProcessingResultOrder.uwReferentie;
    fileProcessingResultOrder.dealerCode =
      this.formGroup.get("dealerCode").value ||
      fileProcessingResultOrder.dealerCode;
  }

  private getPrefilledOrderform(file: any): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);
    return this.backend.postPrefilledOrderform(formData);
  }

  private saveChangesToPostModel() {
    const postModel = this.model;
    const form = this.formGroup.value;

    postModel.opdrachtgever = form.opdrachtgever;
    postModel.contactpersoon = form.contactpersoon;
    postModel.telefoonnummer = form.telefoonnummer;
    postModel.emailadres = form.emailadres;
    postModel.uwReferentie = form.uwReferentie;
    postModel.dealerCode = form.dealerCode;
    postModel.afleverdatum = form.afleverdatum;
    postModel.opmerkingen = form.opmerkingen;
    postModel.bijlage = form.bijlage.map((value) => {
      return value[0].name || "";
    });

    this.preserveWelkomFormValues(PRESERVED_FORM_FIELDS);
  }

  private buildForm(): FormGroup {
    const postModel = this.model;
    const formGroup = this.formBuilder.group({
      opdrachtgever: [postModel.opdrachtgever || "", []],
      contactpersoon: [postModel.contactpersoon || "", [Validators.required]],
      telefoonnummer: [
        postModel.telefoonnummer || "",
        [Validators.maxLength(10), Validators.minLength(10)],
      ],
      emailadres: [
        postModel.emailadres || "",
        [Validators.required, Validators.email],
      ],
      uwReferentie: [postModel.uwReferentie || ""],
      dealerCode: [postModel.dealerCode || "", [Validators.required]],
      afleverdatum: [
        formatDate(postModel.afleverdatum || new Date(), "yyyy-MM-dd", "en"),
        [],
      ],
      opmerkingen: [postModel.opmerkingen || ""],
      bijlage: this.buildBijlageFormArray(postModel.bijlage),
      prefilledOrderformFile: [postModel.prefilledOrderformFile],
    });

    return formGroup;
  }

  public buildBijlageFormArray(fileNames?: string[]): FormArray {
    if (!fileNames || !fileNames.length) {
      return this.formBuilder.array([
        // this.formBuilder.control(["", [FileFormatValidator.validate]])
      ]);
    }

    let controls: FormControl[] = [];

    fileNames.forEach((name) => {
      controls.push(
        this.formBuilder.control([name || "", [FileFormatValidator.validate]])
      );
    });

    return this.formBuilder.array(controls);
  }

  private validateForm() {
    if (!this.formGroup.valid && !environment.disableFieldValidation) {
      Object.keys(this.formGroup.controls).forEach((field) => {
        const control = this.formGroup.get(field);
        control.markAsTouched();
      });
      return false;
    }

    return true;
  }
}

const ValidationMessages = {
  opdrachtgever: {
    required: "Opdrachtgever is een verplicht veld",
  },
  contactpersoon: {
    required: "Contactpersoon is een verplicht veld",
  },
  emailadres: {
    required: "E-mailadres is een verplicht veld.",
    emailadres: "Dit is geen geldig email adres.",
  },
  telefoonnummer: {
    required: "Telefoonnummer is een verplicht veld",
    maxlength: "Het telefoonnummer moet uit 10 getallen bestaan.",
    minlength: "Het telefoonnummer moet uit 10 getallen bestaan.",
  },
  dealerCode: {
    required: "Dealer is een verplicht veld.",
  },
  bijlage: {
    acceptedFile:
      "Dit is geen ondersteund bestandstype. Geaccepteerde bestandstypen: *.pdf, *.txt, *.doc, *.docx",
  },
};
